import { useState } from 'react';
import Hero from './components/Hero';
import Problems from './components/Problems';
import Benefits from './components/Benefits';
import Solutions from './components/Solutions';
import Credibility from './components/Credibility';
import Comparison from './components/Comparison';
import FAQ from './components/FAQ';
import Closing from './components/Closing';
import Footer from './components/Footer';

export default function Home() {
  const [showBookingModal, setShowBookingModal] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');

  const handleBookingClick = () => {
    setShowBookingModal(true);
    setSubmitStatus('idle');
  };

  const handleCloseModal = () => {
    setShowBookingModal(false);
    setSubmitStatus('idle');
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setSubmitStatus('submitting');

    const form = e.currentTarget;
    const formData = new FormData(form);
    
    // テキストエリアの文字数チェック
    const concerns = formData.get('concerns') as string;
    if (concerns && concerns.length > 500) {
      alert('ご相談内容は500文字以内でご入力ください');
      setSubmitStatus('idle');
      return;
    }

    // セレクトボックスの選択内容を取得
    const clinicSelect = form.querySelector('#clinic') as HTMLSelectElement;
    if (clinicSelect) {
      formData.set('clinic', clinicSelect.options[clinicSelect.selectedIndex].text);
    }

    try {
      const response = await fetch('https://readdy.ai/api/form/d4duvr5j00g4h7jvfakg', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: new URLSearchParams(formData as any).toString(),
      });

      if (response.ok) {
        setSubmitStatus('success');
        form.reset();
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <Hero onBookingClick={handleBookingClick} />
      <Problems />
      <Benefits />
      <Solutions />
      <Credibility />
      <Comparison />
      <FAQ />
      <Closing onBookingClick={handleBookingClick} />
      <Footer />
      
      {showBookingModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={handleCloseModal}>
          <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex justify-between items-center">
              <h3 className="text-2xl font-bold text-gray-900">無料カウンセリング予約</h3>
              <button onClick={handleCloseModal} className="w-8 h-8 flex items-center justify-center text-gray-400 hover:text-gray-600 transition-colors">
                <i className="ri-close-line text-2xl"></i>
              </button>
            </div>
            <div className="p-6">
              {submitStatus === 'success' ? (
                <div className="text-center py-12">
                  <div className="w-20 h-20 flex items-center justify-center bg-teal-100 rounded-full mx-auto mb-6">
                    <i className="ri-check-line text-5xl text-teal-600"></i>
                  </div>
                  <h4 className="text-2xl font-bold text-gray-900 mb-4">ご予約ありがとうございます！</h4>
                  <p className="text-gray-600 mb-8">
                    担当者より24時間以内にご連絡いたします。<br />
                    しばらくお待ちください。
                  </p>
                  <button onClick={handleCloseModal} className="bg-teal-600 text-white font-bold py-3 px-8 rounded-lg hover:bg-teal-700 transition-colors whitespace-nowrap">
                    閉じる
                  </button>
                </div>
              ) : (
                <form id="booking-form" data-readdy-form onSubmit={handleSubmit} className="space-y-5">
                  <div>
                    <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">お名前 *</label>
                    <input type="text" id="name" name="name" required className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent outline-none transition-all" placeholder="山田 花子" />
                  </div>
                  <div>
                    <label htmlFor="age" className="block text-sm font-medium text-gray-700 mb-2">年齢 *</label>
                    <input type="number" id="age" name="age" required min="18" max="100" className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent outline-none transition-all" placeholder="30" />
                  </div>
                  <div>
                    <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-2">電話番号 *</label>
                    <input type="tel" id="phone" name="phone" required className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent outline-none transition-all" placeholder="090-1234-5678" />
                  </div>
                  <div>
                    <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">メールアドレス *</label>
                    <input type="email" id="email" name="email" required className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent outline-none transition-all" placeholder="example@email.com" />
                  </div>
                  <div>
                    <label htmlFor="clinic" className="block text-sm font-medium text-gray-700 mb-2">希望クリニック *</label>
                    <select id="clinic" name="clinic" required className="w-full px-4 py-3 pr-10 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent outline-none transition-all appearance-none bg-white cursor-pointer" style={{backgroundImage: "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 24 24' stroke='%236b7280'%3E%3Cpath stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='M19 9l-7 7-7-7'%3E%3C/path%3E%3C/svg%3E\")", backgroundRepeat: "no-repeat", backgroundPosition: "right 0.75rem center", backgroundSize: "1.5em 1.5em"}}>
                      <option value="">選択してください</option>
                      <option value="LIOデンタルクリニック">LIOデンタルクリニック</option>
                      <option value="いくの歯科医院">いくの歯科医院</option>
                    </select>
                  </div>
                  <div>
                    <label htmlFor="preferred-date" className="block text-sm font-medium text-gray-700 mb-2">希望日時（第一希望） *</label>
                    <input type="datetime-local" id="preferred-date" name="preferred-date" required className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent outline-none transition-all" />
                  </div>
                  <div>
                    <label htmlFor="preferred-date-2" className="block text-sm font-medium text-gray-700 mb-2">希望日時（第二希望）</label>
                    <input type="datetime-local" id="preferred-date-2" name="preferred-date-2" className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent outline-none transition-all" />
                  </div>
                  <div>
                    <label htmlFor="concerns" className="block text-sm font-medium text-gray-700 mb-2">気になっていること・ご相談内容</label>
                    <textarea id="concerns" name="concerns" rows={4} maxLength={500} className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent outline-none transition-all resize-none" placeholder="歯並びで気になっていることや、ご質問などをお書きください（500文字以内）"></textarea>
                    <p className="text-xs text-gray-500 mt-1">500文字以内でご入力ください</p>
                  </div>
                  <div className="pt-4">
                    <button 
                      type="submit" 
                      disabled={submitStatus === 'submitting'}
                      className="w-full bg-gradient-to-r from-teal-500 to-teal-600 text-white font-bold py-4 px-8 rounded-lg hover:from-teal-600 hover:to-teal-700 transition-all duration-300 shadow-lg hover:shadow-xl whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {submitStatus === 'submitting' ? '送信中...' : '無料カウンセリングを予約する'}
                    </button>
                    {submitStatus === 'error' && (
                      <p className="text-red-600 text-sm text-center mt-3">送信に失敗しました。もう一度お試しください。</p>
                    )}
                    {submitStatus === 'idle' && (
                      <p className="text-xs text-gray-500 text-center mt-3">送信後、担当者より24時間以内にご連絡いたします</p>
                    )}
                  </div>
                </form>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}